import { CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Select } from '@mui/material';
import { useEffect } from 'react';
import { Control, FieldError, Path, RegisterOptions, UseFormWatch, useController } from 'react-hook-form';

type FormSelectProps<T, R> = {
    label: string;
    name: Path<T>;
    control: Control<T>;
    componentProps?: {
        type?: string;
        startAdornment?: JSX.Element;
        endAdornment?: JSX.Element;
    };
    rules?: Omit<RegisterOptions<T>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
    required?: boolean;
    fieldError?: FieldError;
    options: FormSelectOption<R>[];
    isLoading?: boolean;
    watch?: UseFormWatch<T>;
    sideEffectHandler?: () => void;
    disabled?: boolean;
};

export type FormSelectOption<T> = {
    value: string;
    displayText: string;
    original?: T;
};

export function FormSelect<T, R>({
    label,
    fieldError,
    name,
    control,
    rules,
    options,
    isLoading = false,
    watch,
    sideEffectHandler,
    disabled,
}: FormSelectProps<T, R>) {
    const {
        field: { ref, ...inputProps },
    } = useController({
        name,
        control,
        rules,
    });
    const watchedValue = watch ? watch(name) : '';
    useEffect(() => {
        if (watchedValue !== '' && sideEffectHandler) {
            sideEffectHandler();
        }
    }, [watchedValue, sideEffectHandler]);

    return (
        <FormControl fullWidth>
            <InputLabel>{label + (!!rules?.required ? ' *' : '')}</InputLabel>
            <Select
                label={label + (!!rules?.required ? ' *' : '')}
                {...inputProps}
                inputRef={ref}
                endAdornment={
                    isLoading && (
                        <InputAdornment position='end' sx={{ marginRight: '1.5rem' }}>
                            <CircularProgress size={20} />
                        </InputAdornment>
                    )
                }
                disabled={isLoading || disabled}
                error={!!fieldError}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.displayText}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
